import type AvatarEditor from 'react-avatar-editor';
import { Button, ButtonGroup, Modal, ModalBody, ModalFooter } from '@george-labs.com/design-system';
import type { RefObject } from 'react';
import { forwardRef } from 'react';

import { t } from '@luna/i18n';
import { useNotifications } from '@shared/snackbar';

import { ImageUploadEditor } from '../components/ImageUploadEditor';
import { useImageUploadContext } from '../ImageUploadContext';
import { ImageUploadModal } from '../ImageUploadService';

interface Props {
    avatarEditor: RefObject<AvatarEditor | null>;
    onUploadedImageChange: () => void;
    showResultAsNotification?: boolean;
    onImageDataUrlChange?: (image?: string) => void;
    onImageUpload: (image: string) => Promise<void>;
}

export const ImageUploadResizeModal = forwardRef<AvatarEditor, Props>(
    (
        {
            avatarEditor,
            onImageDataUrlChange,
            onUploadedImageChange,
            onImageUpload,
            showResultAsNotification = false,
        },
        ref,
    ) => {
        const {
            currentModal,
            imageUrl,
            closeModal,
            openLoadingModal,
            openErrorModal,
            openUploadSuccessModal,
        } = useImageUploadContext();
        const { displaySuccess } = useNotifications('image-upload');

        if (currentModal !== ImageUploadModal.ResizeModal) {
            return null;
        }

        const handleImageSave = async () => {
            openLoadingModal();

            if (!ref) {
                return;
            }

            const editor = avatarEditor.current as AvatarEditor;

            if (!editor) {
                return;
            }

            const newImageDataUrl = editor.getImageScaledToCanvas().toDataURL();

            if (!newImageDataUrl) {
                openErrorModal();
                return;
            }

            onImageDataUrlChange?.(newImageDataUrl);

            onImageUpload(newImageDataUrl)
                .then(() => {
                    onUploadedImageChange();
                    if (showResultAsNotification) {
                        displaySuccess(t('main.imageUpload.successUpdateModalBody'));
                        closeModal();
                        return;
                    }
                    openUploadSuccessModal();
                })
                .catch(openErrorModal);
        };

        return (
            <Modal
                isOpen
                appear
                headerIcon={Modal.HEADER_ICON.CAMERA}
                title={t('main.imageUpload.resizePhotoModalTitle')}
                onClose={closeModal}
                data-cy="image-upload-editor"
            >
                <ModalBody>
                    <ImageUploadEditor ref={ref} imageUrl={imageUrl} />
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <Button
                            data-cy="cancel-image-upload-button"
                            onClick={closeModal}
                            variant={Button.VARIANT.SECONDARY}
                        >
                            {t('app.cancel')}
                        </Button>
                        <Button data-cy="confirm-image-upload-button" onClick={handleImageSave}>
                            {t('app.save')}
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        );
    },
);
